import React from 'react';
import _ from 'lodash';

import { Timeline } from 'modules';
import { DateTime } from 'luxon';
import { useTier1ReturnAuthorizationLifeCycleWidgetQuery } from 'generated/graphql';



export interface IData {
  steps: Timeline.IStep[];
}

export function useQuery(returnAuthorizationId: string) {
  const tuple = useTier1ReturnAuthorizationLifeCycleWidgetQuery({
    variables: {
      returnAuthorizationId
    }
  });

  return React.useMemo(
    () => {
      const data: IData = {
        steps: []
      };
      const returnAuthorization = tuple.data?.tier1ReturnAuthorization;
      if (!returnAuthorization) {
        return {
          ...tuple,
          data
        };
      }

      const {
        carton,
        observation,
        itemReceipt,
        qualityControlCheck,
        selectedTriage
      } = returnAuthorization;
      const workflowType = selectedTriage?.workflow.type.name;
      const isQCExemptWorkflow = workflowType === 'credit' || workflowType === 'scrap';
      const isClosed = !_.isNil(carton) || isQCExemptWorkflow;

      const steps: Timeline.IStep[] = _.compact([
        {
          label: "Pre-Alert Acknowledged",
          approvalType: 'approved',
          completedAt: DateTime.fromISO(returnAuthorization.createdTimestamp)
        },
        {
          label: "RMA Received",
          approvalType: itemReceipt ? 'approved' : 'pending',
          completedAt: itemReceipt ? DateTime.fromISO(itemReceipt.createdTimestamp) : null
        },
        {
          label: "RMA Triaged",
          approvalType: observation ? 'approved' : 'pending',
          completedAt: observation ? DateTime.fromISO(observation.createdTimestamp) : null
        },
        {
          label: selectedTriage?.workflow.type
            ? selectedTriage?.workflow.type.displayName
            : "Triage workflow",
          approvalType: selectedTriage ? 'approved' : 'pending',
          completedAt: selectedTriage?.createdTimestamp
            ? DateTime.fromISO(selectedTriage.createdTimestamp)
            : null
        },

        // Replacements, and returns with serial #s that don't match should have a Quality Control step.
        (workflowType === 'replacement' || (workflowType === 'return' && itemReceipt?.serialNumber !== selectedTriage?.replacementSerialNumber)) ? {
          label: "Quality Control",
          approvalType: qualityControlCheck ? 'approved' : 'pending',
          completedAt: qualityControlCheck?.createdTimestamp ? DateTime.fromISO(qualityControlCheck.createdTimestamp) : null
        } : null,

        {
          label: "RMA Closed",
          approvalType: isClosed ? 'approved' : 'pending',
          completedAt: carton ?
            DateTime.fromISO(carton.createdTimestamp) :
            (isQCExemptWorkflow && selectedTriage) ?
              DateTime.fromISO(selectedTriage.createdTimestamp) :
              null
        }
      ]);

      data.steps = steps;

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
